import { motion } from "framer-motion";
import { FaRecycle, FaChartLine, FaDollarSign, FaChartPie, FaUsers, FaLeaf } from "react-icons/fa";
import FAQ from './FAQ';
import { Link } from 'react-router-dom';
import Invest_portfolio from '../assets/stock_market.png';
import Header from "./Header";
import Footer from "./Footer";

const InvestPortfolio = () => {
   return (
      <>
         <Header />

         {/* Hero Section */}
         <div className="bg-[#f3f0eb]">
            <section className="relative flex flex-col md:flex-row items-center justify-between max-w-7xl mx-auto px-4 py-12">

               <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  className="md:w-1/2 space-y-6"
               >

                  <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-[#1a4632] mb-8">
                     Turn Your <span className="text-[#9cc026]">Recycling Rewards</span> Into Useful Benefits
                  </h2>

                  <p className="text-lg md:text-xl mb-6">
                     At FareFuel, recycling isn’t just good for the planet — it also helps you earn redeemable rewards.
                     Use your rewards balance toward supported services and everyday needs.
                  </p>

                  <div className="mt-8">
                     <Link
                        to="/register"
                        className="inline-block px-6 py-2 bg-gradient-to-r from-green-400 to-green-600 text-white text-lg font-semibold rounded-2xl shadow-lg hover:shadow-green-500/50 hover:scale-105 transition-all duration-300"
                     >
                        Get Started
                     </Link>
                     <p className="mt-2 text-sm text-gray-600 italic">
                        No hidden fees. Simple rewards. Eco-friendly impact.
                     </p>
                  </div>

               </motion.div>

               <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  className="md:w-1/2 flex justify-center mt-10 md:mt-0"
               >
                  <img
                     src={Invest_portfolio}
                     alt="Rewards Illustration"
                     className="w-80 md:w-[400px] drop-shadow-xl"
                  />
               </motion.div>

            </section>
         </div>


         {/* How It Works */}
         <section className="py-16 px-4 bg-white">
            <div className="max-w-7xl mx-auto text-center mb-12">
               <h2 className="text-3xl md:text-4xl font-bold text-center text-[#1a4632] mb-6">
                  How It <span className="text-[#9cc026]">Works</span>
               </h2>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">

               <FAQ
                  question={
                     <span className="flex items-center gap-2">
                        <FaRecycle className="text-green-600" /> Recycle with FareFuel
                     </span>
                  }
                  answer={
                     <p className="mb-2">
                        Drop off recyclables and earn redeem credits through your Redemption Center.
                     </p>
                  }
               />

               <FAQ
                  question={
                     <span className="flex items-center gap-2">
                        <FaDollarSign className="text-green-600" /> Transfer Your Redeem Credit
                     </span>
                  }
                  answer={
                     <p className="mb-2">
                        Instead of cash, choose to transfer your redeem credit from your dashboard.
                     </p>
                  }
               />

               <FAQ
                  question={
                     <span className="flex items-center gap-2">
                        <FaChartPie className="text-green-600" /> Use Your Rewards
                     </span>
                  }
                  answer={
                     <p className="mb-2">
                        Use your rewards balance toward supported services and available redemption options.
                     </p>
                  }
               />

               <FAQ
                  question={
                     <span className="flex items-center gap-2">
                        <FaChartLine className="text-green-600" /> Track Your Activity
                     </span>
                  }
                  answer={
                     <p className="mb-2">
                        Monitor your rewards usage and transaction history directly in your FareFuel account.
                     </p>
                  }
               />

            </div>
         </section>

         {/* Why Invest Your Refund */}
         <section className="py-16 px-6 bg-[#f6f9f1]">
            <div className="max-w-7xl mx-auto text-center mb-12">
               <h2 className="text-3xl font-bold mb-4">Why Use Your Rewards?</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
               {[
                  {
                     icon: <FaChartLine className="text-green-600 text-4xl mb-4" />,
                     title: "Start Small, Get More Value",
                     desc: "Even small recycling rewards can be used toward useful services and everyday needs."
                  },
                  {
                     icon: <FaUsers className="text-green-600 text-4xl mb-4" />,
                     title: "Accessible to Everyone",
                     desc: "No minimum requirements. Simply recycle and earn redeemable rewards."
                  },
                  {
                     icon: <FaLeaf className="text-green-600 text-4xl mb-4" />,
                     title: "Eco + Smart Usage",
                     desc: "Help the environment while making the most of your rewards balance."
                  }
               ].map((card, idx) => (
                  <motion.div
                     key={idx}
                     whileHover={{ y: -5 }}
                     className="bg-[#ECFFDC] border border-[#25994c] rounded-lg p-6 shadow-sm transition duration-300 ease-in-out hover:shadow-[0_8px_30px_4px_rgba(116,175,32,0.4)]"
                  >
                     {card.icon}
                     <h3 className="text-xl font-semibold mb-2">{card.title}</h3>
                     <p className="text-gray-600">{card.desc}</p>
                  </motion.div>
               ))}
            </div>
         </section>

         {/* Example Section */}
         <section className="py-12 px-6 bg-gradient-to-r bg-[#9cc026] text-white text-center">
            <motion.h3
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               transition={{ duration: 1 }}
               className="text-2xl md:text-3xl font-bold mb-4"
            >
               💡 Example
            </motion.h3>

            <p className="text-lg max-w-3xl mx-auto">
               Recycle 100 bottles → Earn $5 in redeem credit → Use your rewards toward supported services.
            </p>

            <div className="mt-8">
               <Link
                  to="/register"
                  className="inline-block px-6 py-2 bg-gradient-to-r from-green-400 to-green-600 text-white text-lg font-semibold rounded-2xl shadow-lg hover:shadow-green-500/50 hover:scale-105 transition-all duration-300"
               >
                  Register
               </Link>
            </div>
         </section>
         <Footer />
      </>
   );
};

export default InvestPortfolio;
